// MultiSelect.jsx
import React, { useState, useEffect } from "react";
import styles from "./MultiSelect.module.scss";

/**
 * Многоразовый компонент MultiSelect с возможностью мультивыбора
 *
 * @param {Object} props
 * @param {Array} props.data - массив данных для выбора
 * @param {Array} props.selectedItems - предварительно выбранные элементы
 * @param {Function} props.onChange - функция обратного вызова при изменении выбора (selectedItems) => void
 * @param {Function} props.getDisplayValue - функция для получения отображаемого значения элемента (item) => string
 * @param {Function} props.getSearchValues - функция для получения значений для поиска (item) => string[]
 * @param {Function} props.getUniqueId - функция для получения уникального идентификатора (item) => string
 * @param {Function} props.getItemDetails - необязательная функция для получения дополнительных деталей элемента (item) => string
 * @param {string} props.placeholder - текст в placeholder
 * @param {string} props.searchPlaceholder - текст в placeholder поиска
 * @param {string} props.noResultsText - текст при отсутствии результатов
 * @param {string} props.clearSelectionText - текст кнопки очистки выбора
 * @param {string} props.resultsFoundText - текст найденных результатов
 */
const MultiSelect = ({
  data = [],
  selectedItems: externalSelectedItems = [],
  onChange,
  getDisplayValue = (item) => item.toString(),
  getSearchValues = (item) => [getDisplayValue(item)],
  getUniqueId = (item) => item.id || item.toString(),
  getItemDetails = null,
  placeholder = "Выберите элементы...",
  searchPlaceholder = "Поиск...",
  noResultsText = "Ничего не найдено",
  clearSelectionText = "Очистить выбор",
  resultsFoundText = "Найдено",
}) => {
  const [selectedItems, setSelectedItems] = useState(externalSelectedItems);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Синхронизация с внешним состоянием
  useEffect(() => {
    setSelectedItems(externalSelectedItems);
  }, [externalSelectedItems]);

  // Функция для фильтрации данных по поисковому запросу
  const filteredData = data.filter((item) => {
    if (!searchTerm) return true;

    const searchTermLower = searchTerm.toLowerCase();
    // Получаем все значения для поиска и проверяем, содержит ли хотя бы одно из них поисковый запрос
    return getSearchValues(item).some((value) =>
      value.toString().toLowerCase().includes(searchTermLower)
    );
  });

  // Функция для выбора/отмены выбора элемента
  const toggleItem = (item) => {
    const itemId = getUniqueId(item);
    let newSelectedItems;

    if (
      selectedItems.some((selectedItem) => getUniqueId(selectedItem) === itemId)
    ) {
      newSelectedItems = selectedItems.filter(
        (selectedItem) => getUniqueId(selectedItem) !== itemId
      );
    } else {
      newSelectedItems = [...selectedItems, item];
    }

    setSelectedItems(newSelectedItems);

    // Вызываем callback, если он предоставлен
    if (onChange) {
      onChange(newSelectedItems);
    }
  };

  // Функция для проверки, выбран ли элемент
  const isSelected = (item) => {
    return selectedItems.some(
      (selectedItem) => getUniqueId(selectedItem) === getUniqueId(item)
    );
  };

  // Функция для очистки всех выбранных элементов
  const clearSelection = () => {
    setSelectedItems([]);
    if (onChange) {
      onChange([]);
    }
  };

  // Обработчик клика вне компонента для закрытия выпадающего списка
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && !e.target.closest(`.${styles.container}`)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.container}>
      <div className={styles.selectWrapper}>
        {/* Поле выбора */}
        <div className={styles.selectField} onClick={() => setIsOpen(!isOpen)}>
          {selectedItems.length === 0 ? (
            <span className={styles.placeholder}>{placeholder}</span>
          ) : (
            <div className={styles.selectedItemsList}>
              {selectedItems.map((item) => (
                <div key={getUniqueId(item)} className={styles.selectedItem}>
                  <span>{getDisplayValue(item)}</span>
                  <button
                    className={styles.removeButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleItem(item);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Выпадающий список */}
        {isOpen && (
          <div className={styles.dropdown}>
            <div className={styles.searchContainer}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {filteredData.length > 0 ? (
              <>
                <div className={styles.dropdownHeader}>
                  <span className={styles.resultsCount}>
                    {resultsFoundText}: {filteredData.length}
                  </span>
                  {selectedItems.length > 0 && (
                    <button
                      className={styles.clearButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSelection();
                      }}
                    >
                      {clearSelectionText} ({selectedItems.length})
                    </button>
                  )}
                </div>
                <ul className={styles.itemsList}>
                  {filteredData.map((item) => (
                    <li
                      key={getUniqueId(item)}
                      className={`${styles.item} ${
                        isSelected(item) ? styles.selectedListItem : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleItem(item);
                      }}
                    >
                      <div className={styles.itemContent}>
                        <input
                          type="checkbox"
                          checked={isSelected(item)}
                          onChange={() => {}}
                          className={styles.checkbox}
                        />
                        <div className={styles.itemDetails}>
                          <div className={styles.itemName}>
                            {getDisplayValue(item)}
                          </div>
                          {getItemDetails && (
                            <div className={styles.itemDescription}>
                              {getItemDetails(item)}
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className={styles.noResults}>{noResultsText}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;
